// @ts-ignore
import { ANSWER_STATUSES, PROBLEM_INNER_TYPES } from '@yandex-int/k-common/constants';
import { ProblemAnswer, LessonProblem } from '@yandex-int/k-common/typings';
import produce from 'immer';
import last from 'lodash/last';
import { createSelector } from 'reselect';

import * as lessonSelectors from 'common.components/lesson/selectors';
import * as resultsSelectors from 'common.components/results/selectors';
import { LessonProblemWithResult } from 'common.components/student-lesson-stat/typings';
import { getStudentCLessonResult } from 'store/student-clesson-result/selectors';
import { getProblemIdFromProps, getStudentIdFromProps } from 'utils/selectors/selectors';

import { LessonRunResult } from './typings';

export const getResults = createSelector(
    [lessonSelectors.getClessonResults, getStudentIdFromProps],
    (clessonResults, studentId) => {
        if (!studentId || !clessonResults) {
            return null;
        }

        return clessonResults.students[studentId] || null;
    }
);

/* eslint-disable max-nested-callbacks */
export const getLessonProblemsWithResult = createSelector(
    [
        resultsSelectors.getStudentCommonProblems,
        lessonSelectors.getIsLessonEvaluable,
        lessonSelectors.getIsLessonEvaluated,
        getStudentCLessonResult,
    ],
    // eslint-disable-next-line max-params
    (problems, isEvaluable, isEvaluated, result) =>
        problems.map((problem) =>
            produce(problem as LessonProblemWithResult, (problemLink) => {
                const { id: problemLinkId } = problemLink;

                const attempts = (result?.answers && result.answers[problemLinkId]) || [];
                const completedAttempts = attempts.filter((a) => a.completed);
                const currentAttempt = attempts[attempts.length - 1];
                const lastCompletedAttempt = completedAttempts[completedAttempts.length - 1] || {
                    status: null,
                };
                const isStatusUndefined = isProblemWithUndefinedStatus(problem, attempts, isEvaluable, isEvaluated);

                if (isStatusUndefined && currentAttempt?.markers) {
                    Object.keys(currentAttempt.markers).forEach((key) => {
                        const marker = { ...currentAttempt.markers[key] };
                        const completedMarker = lastCompletedAttempt.markers[key] || {};

                        // успешные статусы подставляются из последней завершённой попытки
                        if (completedMarker.status === ANSWER_STATUSES.CORRECT) {
                            marker.status = completedMarker.status;
                            marker.answer_status = completedMarker.answer_status;
                        }
                    });
                }

                if (problemLink.problem) {
                    problemLink.problem.attempt = lastCompletedAttempt;
                }
            })
        )
);
/* eslint-enable max-nested-callbacks */

function isProblemWithUndefinedStatus(
    problemLink: LessonProblem,
    attempts: Array<ProblemAnswer>,
    isEvaluable: boolean,
    isEvaluated: boolean
): boolean {
    const {
        options: { max_attempts: maxAttempts },
    } = problemLink;
    const completedAttempts = attempts.filter((a) => a.completed);

    if (!isEvaluable) {
        if (!completedAttempts.some((a) => a.status === ANSWER_STATUSES.CORRECT)) {
            return false;
        }

        const fails = completedAttempts.filter((a) => a.status === ANSWER_STATUSES.INCORRECT).length;
        const notEvaluated = completedAttempts.filter((a) => a.status === ANSWER_STATUSES.NOT_EVALUATED).length;

        if (notEvaluated === maxAttempts || fails + notEvaluated === maxAttempts) {
            return false;
        }
    }

    return !isEvaluated;
}

export const lessonRunResultSelector = createSelector(
    [getStudentCLessonResult, getLessonProblemsWithResult],
    (result, problems): LessonRunResult => {
        const answers = result?.answers || {};
        const problemsNumber = (problems || []).filter(
            (problem) => problem.problem.type === PROBLEM_INNER_TYPES.PROBLEM
        ).length;

        return {
            correctNumber: countAnswersByStatus(answers, ANSWER_STATUSES.CORRECT),
            incorrectNumber: countAnswersByStatus(answers, ANSWER_STATUSES.INCORRECT),
            maxPoints: result?.max_points,
            points: result?.points,
            problemsNumber,
        };
    }
);

export const hasStudentPastedIntoProblem = createSelector(
    [getStudentCLessonResult, getProblemIdFromProps],
    (studentCLessonResult, problemId) => !!studentCLessonResult?.problem_solve_sessions[problemId!]?.pasted
);

function countAnswersByStatus(answers: Record<number, ProblemAnswer[]>, status: ANSWER_STATUSES) {
    return Object.keys(answers).reduce((acc, answerKey) => {
        const lastCompletedAttempt = last(answers[Number(answerKey)].filter((answer) => answer.completed));
        const lastCompletedAttemptStatus =
            lastCompletedAttempt && Number.isInteger(lastCompletedAttempt.status)
                ? lastCompletedAttempt.status
                : ANSWER_STATUSES.NOT_EVALUATED;

        if (lastCompletedAttemptStatus === status) {
            return acc + 1;
        }

        return acc;
    }, 0);
}
